import React, { useEffect, useMemo, useState } from 'react';
import './AdminCatalog.css';

const LEVELS = ['L1', 'L2', 'L3', 'M1', 'M2', 'DU', 'MASTERE'];
const STORAGE_YEARS_KEY = 'fjcAdminAvailableYears';
const MAX_VISIBLE_COLUMNS = 4;

function readStoredList(key) {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function persistList(key, values) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(values));
  } catch (error) {
    // ignore storage errors
  }
}

function mergeUnique(primary, extra) {
  const seen = new Set();
  const merged = [];
  [...primary, ...extra].forEach((value) => {
    if (value === null || value === undefined) return;
    const text = String(value).trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    merged.push(text);
  });
  return merged;
}

function areArraysEqual(left, right) {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i += 1) {
    if (left[i] !== right[i]) return false;
  }
  return true;
}

function normalizeAvailableYears(values) {
  if (!Array.isArray(values)) return [];
  const seen = new Set();
  const normalized = [];
  values.forEach((value) => {
    if (value === null || value === undefined) return;
    const text = String(value).trim();
    if (!text || text.toLowerCase() === 'autre') return;
    if (seen.has(text)) return;
    seen.add(text);
    normalized.push(text);
  });
  return normalized;
}

function collectYearsFromEntries(entries) {
  const years = [];
  entries.forEach((entry) => {
    (entry.years || []).forEach((year) => {
      if (year !== null && year !== undefined && year !== 'Autre') {
        years.push(year);
      }
    });
  });
  return years;
}

function entryToEditable(entry, fallbackIndex, level) {
  const isSection = !Array.isArray(entry) || entry.length < 4;
  const code = Array.isArray(entry) ? entry[0] : '';
  const labels = Array.isArray(entry?.[1]) ? entry[1] : [];
  const years = Array.isArray(entry?.[2]) ? entry[2] : [];
  const tracks = Array.isArray(entry?.[3]) ? entry[3] : [];

  return {
    id: `${Date.now()}-${fallbackIndex}-${Math.random().toString(16).slice(2)}`,
    kind: isSection ? 'section' : 'program',
    code: code ?? '',
    labels,
    years,
    tracks,
    levels: level ? [level] : []
  };
}

function cleanLines(values, { preserveHoles }) {
  if (!Array.isArray(values)) {
    return [];
  }
  const cleaned = values.map((item) => {
    if (item === null || item === undefined) {
      return null;
    }
    const text = String(item).trim();
    return text === '' ? null : text;
  });

  if (!preserveHoles) {
    return cleaned.filter((item) => item !== null);
  }

  let end = cleaned.length - 1;
  while (end >= 0 && cleaned[end] === null) {
    end -= 1;
  }
  return cleaned.slice(0, end + 1);
}

function editableToEntry(entry) {
  const labels = cleanLines(entry.labels, { preserveHoles: false });
  const years = ensureAutre(cleanLines(entry.years, { preserveHoles: true }));
  const base = [entry.code, labels, years];
  if (entry.kind !== 'section') {
    base.push(cleanLines(entry.tracks, { preserveHoles: false }));
  }
  return base;
}

function normalizeLinesEditable(value, { preserveHoles }) {
  const raw = value.split('\n');
  return raw.map((line) => {
    if (line.trim() === '') {
      return preserveHoles ? null : '';
    }
    return line;
  });
}

function linesToText(values, { preserveHoles }) {
  if (!Array.isArray(values)) {
    return '';
  }
  if (preserveHoles) {
    return values.map((item) => (item === null ? '' : item)).join('\n');
  }
  return values.filter((item) => item !== null && item !== undefined).join('\n');
}

function sortLevels(values) {
  if (!Array.isArray(values)) return [];
  const set = new Set(values);
  return LEVELS.filter((level) => set.has(level));
}

function ensureAutre(values) {
  const cleaned = Array.isArray(values) ? values.filter((item) => item !== null && item !== undefined) : [];
  if (!cleaned.includes('Autre')) {
    return [...cleaned, 'Autre'];
  }
  return cleaned;
}

function entryKey(entry) {
  return JSON.stringify(editableToEntry({
    kind: entry.kind,
    code: entry.code,
    labels: entry.labels,
    years: entry.years,
    tracks: entry.tracks || []
  }));
}

function splitIntoColumns(entries) {
  const columns = [];
  let current = [];

  entries.forEach((entry) => {
    if (entry.kind === 'section') {
      if (current.length) {
        columns.push(current);
      }
      current = [entry];
      return;
    }

    if (!current.length && !columns.length) {
      current = [entry];
      return;
    }

    current.push(entry);
  });

  if (current.length) {
    columns.push(current);
  }

  return columns;
}

export default function AdminCatalog() {
  const [entries, setEntries] = useState([]);
  const [codeDrafts, setCodeDrafts] = useState({});
  const [token, setToken] = useState(() => localStorage.getItem('fjcAdminToken') || '');
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [dirty, setDirty] = useState(false);
  const [jsPreview, setJsPreview] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [columnOffset, setColumnOffset] = useState(0);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [availableYears, setAvailableYears] = useState(() => {
    const stored = readStoredList(STORAGE_YEARS_KEY);
    return normalizeAvailableYears(stored);
  });
  const [yearDraft, setYearDraft] = useState('');
  const [draggedYear, setDraggedYear] = useState(null);
  const [dragOverYear, setDragOverYear] = useState(null);

  const entryList = useMemo(() => entries, [entries]);
  const columns = useMemo(() => splitIntoColumns(entryList), [entryList]);
  const visibleColumns = useMemo(
    () => columns.slice(columnOffset, columnOffset + MAX_VISIBLE_COLUMNS),
    [columns, columnOffset]
  );

  useEffect(() => {
    loadCatalog();
  }, []);

  useEffect(() => {
    persistList(STORAGE_YEARS_KEY, availableYears);
  }, [availableYears]);

  async function loadCatalog() {
    setStatus({ type: 'loading', message: 'Chargement du catalogue…' });
    try {
      const res = await fetch('/backend/api/formulaire-je-candidate/catalog?format=json');
      if (!res.ok) {
        throw new Error('Chargement impossible.');
      }
      const data = await res.json();
      const merged = [];
      const keyed = new Map();

      LEVELS.forEach((level) => {
        const outer = Array.isArray(data[level]) ? data[level] : [];
        const inner = Array.isArray(outer[0]) ? outer[0] : [];
        let currentSectionId = null;

        inner.forEach((entry, idx) => {
          const editable = entryToEditable(entry, idx, level);
          const key = entryKey(editable);
          const existing = keyed.get(key);
          if (existing) {
            if (!existing.levels.includes(level)) {
              existing.levels = sortLevels([...existing.levels, level]);
            }
            if (existing.kind === 'section') {
              currentSectionId = existing.id;
            }
            return;
          }

          editable.levels = sortLevels(editable.levels);

          if (editable.kind === 'section') {
            currentSectionId = editable.id;
            merged.push(editable);
            keyed.set(key, editable);
            return;
          }

          const sectionIndex = currentSectionId
            ? merged.findIndex((item) => item.id === currentSectionId)
            : -1;
          if (sectionIndex >= 0) {
            let insertIndex = merged.length;
            for (let i = sectionIndex + 1; i < merged.length; i += 1) {
              if (merged[i].kind === 'section') {
                insertIndex = i;
                break;
              }
            }
            merged.splice(insertIndex, 0, editable);
          } else {
            merged.push(editable);
          }
          keyed.set(key, editable);
        });
      });

      const storedYears = readStoredList(STORAGE_YEARS_KEY);
      const nextYears = normalizeAvailableYears(
        mergeUnique(storedYears, collectYearsFromEntries(merged))
      );

      setEntries(merged);
      setCodeDrafts({});
      setAvailableYears(nextYears);
      setColumnOffset(0);
      setDirty(false);
      setStatus({ type: 'success', message: 'Catalogue chargé.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Erreur de chargement.' });
    }
  }

  async function saveCatalog() {
    setStatus({ type: 'loading', message: 'Enregistrement en cours…' });

    const entriesToSave = entries.map((entry) => {
      if (!Object.prototype.hasOwnProperty.call(codeDrafts, entry.id)) {
        return entry;
      }
      return { ...entry, code: codeDrafts[entry.id] };
    });

    const payload = {};
    LEVELS.forEach((level) => {
      payload[level] = [
        entriesToSave
          .filter((entry) => (entry.levels || []).includes(level))
          .map((entry) => editableToEntry(entry))
      ];
    });

    try {
      const res = await fetch('/backend/api/formulaire-je-candidate/catalog', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'x-admin-token': token } : {})
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        throw new Error('Enregistrement refusé.');
      }
      setEntries(entriesToSave);
      setCodeDrafts({});
      setDirty(false);
      setStatus({ type: 'success', message: 'Catalogue enregistré.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Erreur lors de la sauvegarde.' });
    }
  }

  async function fetchJsPreview() {
    setStatus({ type: 'loading', message: 'Préparation du format JS…' });
    try {
      const res = await fetch('/backend/api/formulaire-je-candidate/catalog');
      if (!res.ok) {
        throw new Error('Prévisualisation indisponible.');
      }
      const text = await res.text();
      setJsPreview(text);
      setShowPreview(true);
      setStatus({ type: 'success', message: 'Format JS prêt.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Erreur lors de la prévisualisation.' });
    }
  }

  function updateEntry(id, patch) {
    setEntries((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry))
    );
    setDirty(true);
  }

  function updateCodeDraft(id, value) {
    setCodeDrafts((prev) => ({
      ...prev,
      [id]: value
    }));
    setDirty(true);
  }

  function clearCodeDraft(id) {
    setCodeDrafts((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function commitCodeDraft(entry, fallbackValue) {
    const hasDraft = Object.prototype.hasOwnProperty.call(codeDrafts, entry.id);
    const draft = hasDraft ? codeDrafts[entry.id] : fallbackValue;
    if (draft === undefined) return;
    if (draft !== entry.code) {
      updateEntry(entry.id, { code: draft });
    }
    if (hasDraft) {
      clearCodeDraft(entry.id);
    }
  }

  function createEntry(kind, baseIndex, levelsOverride) {
    return {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      kind,
      code: kind === 'section' ? `disabled${baseIndex}` : '',
      labels: [''],
      years: [''],
      tracks: [''],
      levels: Array.isArray(levelsOverride) && levelsOverride.length
        ? levelsOverride
        : [...LEVELS]
    };
  }

  function addProgramToSection(sectionId) {
    setEntries((prev) => {
      const list = [...prev];
      const sectionIndex = list.findIndex((item) => item.id === sectionId);
      if (sectionIndex < 0) {
        return prev;
      }
      const section = list[sectionIndex];
      const baseIndex = list.length + 1;
      const newEntry = createEntry('program', baseIndex, section.levels);
      list.splice(sectionIndex + 1, 0, newEntry);
      return list;
    });
    setDirty(true);
  }

  function duplicateEntry(entry) {
    if (!entry || entry.kind !== 'program') return;
    setEntries((prev) => {
      const list = [...prev];
      const index = list.findIndex((item) => item.id === entry.id);
      if (index < 0) return prev;
      const copy = {
        ...entry,
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        labels: Array.isArray(entry.labels) ? [...entry.labels] : [''],
        years: Array.isArray(entry.years) ? [...entry.years] : [''],
        tracks: Array.isArray(entry.tracks) ? [...entry.tracks] : [''],
        levels: Array.isArray(entry.levels) ? [...entry.levels] : [...LEVELS]
      };
      list.splice(index + 1, 0, copy);
      return list;
    });
    setDirty(true);
  }

  function removeEntry(entry) {
    if (!entry || entry.kind !== 'program') return;
    const label = Array.isArray(entry.labels)
      ? entry.labels.find((item) => item && item.trim())
      : '';
    const message = label
      ? `Supprimer la formation "${label}" ?`
      : 'Supprimer cette formation ?';
    if (!window.confirm(message)) return;
    setEntries((prev) => prev.filter((item) => item.id !== entry.id));
    setDirty(true);
  }

  function findSectionBounds(list, index) {
    let sectionStart = -1;
    for (let i = index; i >= 0; i -= 1) {
      if (list[i].kind === 'section') {
        sectionStart = i;
        break;
      }
    }
    let sectionEnd = list.length;
    for (let i = index + 1; i < list.length; i += 1) {
      if (list[i].kind === 'section') {
        sectionEnd = i;
        break;
      }
    }
    return { sectionStart, sectionEnd };
  }

  function moveEntryWithinSection(id, direction) {
    setEntries((prev) => {
      const list = [...prev];
      const index = list.findIndex((item) => item.id === id);
      if (index < 0) return prev;
      const item = list[index];
      if (item.kind !== 'program') return prev;
      const { sectionStart, sectionEnd } = findSectionBounds(list, index);
      if (sectionStart === -1) return prev;
      const target = index + direction;
      if (target <= sectionStart || target >= sectionEnd) return prev;
      list.splice(index, 1);
      list.splice(target, 0, item);
      return list;
    });
    setDirty(true);
  }

  function moveEntryToAdjacentSection(id, direction) {
    setEntries((prev) => {
      const list = [...prev];
      const index = list.findIndex((item) => item.id === id);
      if (index < 0) return prev;
      const item = list[index];
      if (item.kind !== 'program') return prev;
      const { sectionStart } = findSectionBounds(list, index);
      if (sectionStart === -1) return prev;

      let targetSectionStart = -1;
      if (direction < 0) {
        for (let i = sectionStart - 1; i >= 0; i -= 1) {
          if (list[i].kind === 'section') {
            targetSectionStart = i;
            break;
          }
        }
      } else {
        for (let i = sectionStart + 1; i < list.length; i += 1) {
          if (list[i].kind === 'section') {
            targetSectionStart = i;
            break;
          }
        }
      }

      if (targetSectionStart === -1) return prev;

      let targetSectionEnd = list.length;
      for (let i = targetSectionStart + 1; i < list.length; i += 1) {
        if (list[i].kind === 'section') {
          targetSectionEnd = i;
          break;
        }
      }

      const targetSection = list[targetSectionStart];
      if (targetSection && targetSection.kind === 'section') {
        const targetYears = (targetSection.years || []).filter((year) => year && year !== 'Autre');
        const itemYears = (item.years || []).filter((year) => year && year !== 'Autre');
        list[targetSectionStart] = {
          ...targetSection,
          levels: sortLevels([...(targetSection.levels || []), ...(item.levels || [])]),
          years: ensureAutre(mergeUnique(targetYears, itemYears))
        };
      }

      list.splice(index, 1);
      if (index < targetSectionStart) {
        targetSectionStart -= 1;
      }
      if (index < targetSectionEnd) {
        targetSectionEnd -= 1;
      }
      const insertIndex = targetSectionEnd;
      list.splice(insertIndex, 0, item);
      return list;
    });
    setDirty(true);
  }

  function handleTokenChange(value) {
    setToken(value);
    localStorage.setItem('fjcAdminToken', value);
  }

  function copyPreview() {
    if (!jsPreview) return;
    navigator.clipboard.writeText(jsPreview);
    setStatus({ type: 'success', message: 'Copié dans le presse-papiers.' });
  }

  function formatGroupCount(count) {
    return `${count} formation${count > 1 ? 's' : ''}`;
  }

  function getSectionLabel(entry) {
    const label = Array.isArray(entry.labels)
      ? entry.labels.find((item) => item && item.trim())
      : '';
    if (!label) return 'Faculté';
    return label.replace(/^-+\s*/, '');
  }

  function groupProgramsByCode(programs) {
    const groups = [];
    const map = new Map();

    programs.forEach((entry) => {
      const rawCode = typeof entry.code === 'string' ? entry.code.trim() : '';
      const key = rawCode ? rawCode.toUpperCase() : 'SANS_CODE';
      const label = rawCode || 'Sans code';
      if (!map.has(key)) {
        const group = { key, label, items: [] };
        map.set(key, group);
        groups.push(group);
      }
      map.get(key).items.push(entry);
    });

    return groups;
  }

  function toggleGroup(groupKey) {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: prev[groupKey] !== false ? false : true
    }));
  }

  function orderEntryYearsByList(entry, yearOrder) {
    const selected = (entry.years || []).filter((year) => year && year !== 'Autre');
    const ordered = yearOrder.filter((year) => selected.includes(year));
    return ensureAutre(ordered);
  }

  function reorderEntriesYears(yearOrder) {
    setEntries((prev) => {
      let hasChanged = false;
      const next = prev.map((entry) => {
        const nextYears = orderEntryYearsByList(entry, yearOrder);
        const currentYears = Array.isArray(entry.years) ? entry.years : [];
        if (!areArraysEqual(currentYears, nextYears)) {
          hasChanged = true;
          return { ...entry, years: nextYears };
        }
        return entry;
      });
      if (hasChanged) {
        setDirty(true);
        return next;
      }
      return prev;
    });
  }

  function addAvailableYear(value) {
    const trimmed = value.trim();
    if (!trimmed) return;
    const next = normalizeAvailableYears([...availableYears, trimmed]);
    setAvailableYears(next);
    setYearDraft('');
  }

  function removeAvailableYear(value) {
    const next = availableYears.filter((item) => item !== value);
    setAvailableYears(next);
    reorderEntriesYears(next);
  }

  function moveAvailableYear(fromYear, toYear) {
    if (!fromYear) return;
    const next = [...availableYears];
    const fromIndex = next.indexOf(fromYear);
    if (fromIndex < 0) return;
    next.splice(fromIndex, 1);
    if (!toYear) {
      next.push(fromYear);
    } else {
      const toIndex = next.indexOf(toYear);
      if (toIndex < 0) {
        next.push(fromYear);
      } else {
        next.splice(toIndex, 0, fromYear);
      }
    }
    setAvailableYears(next);
    reorderEntriesYears(next);
  }

  function toggleEntryLevel(entryId, level) {
    setEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== entryId) return entry;
        const nextLevels = new Set(
          (entry.levels || []).filter((item) => LEVELS.includes(item))
        );
        if (nextLevels.has(level)) {
          nextLevels.delete(level);
        } else {
          nextLevels.add(level);
        }
        return {
          ...entry,
          levels: sortLevels([...nextLevels])
        };
      })
    );
    setDirty(true);
  }

  function toggleEntryYear(entryId, year) {
    setEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== entryId) return entry;
        const nextYears = new Set(
          (entry.years || []).filter((item) => item && item !== 'Autre')
        );
        if (nextYears.has(year)) {
          nextYears.delete(year);
        } else {
          nextYears.add(year);
        }
        const ordered = availableYears.filter((item) => nextYears.has(item));
        return {
          ...entry,
          years: ensureAutre(ordered)
        };
      })
    );
    setDirty(true);
  }

  function renderLevelSelector(entry) {
    const selected = new Set(
      (entry.levels || []).filter((level) => LEVELS.includes(level))
    );
    return (
      <div className="fjc-label">
        <span>Niveaux</span>
        <div className="fjc-choice-grid">
          {LEVELS.map((level) => (
            <label
              key={level}
              className={`fjc-choice ${selected.has(level) ? 'is-active' : ''}`}
            >
              <input
                type="checkbox"
                checked={selected.has(level)}
                onChange={() => toggleEntryLevel(entry.id, level)}
              />
              <span>{level}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  function renderYearSelector(entry) {
    const selected = new Set(
      (entry.years || []).filter((year) => year && year !== 'Autre')
    );
    return (
      <div className="fjc-label">
        <span>Années</span>
        {availableYears.length ? (
          <div className="fjc-choice-grid">
            {availableYears.map((year) => (
              <label
                key={year}
                className={`fjc-choice ${selected.has(year) ? 'is-active' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(year)}
                  onChange={() => toggleEntryYear(entry.id, year)}
                />
                <span>{year}</span>
              </label>
            ))}
          </div>
        ) : (
          <p className="fjc-empty">Ajoutez des années dans le référentiel.</p>
        )}
      </div>
    );
  }

  function renderProgramCard(entry) {
    const codeValue = Object.prototype.hasOwnProperty.call(codeDrafts, entry.id)
      ? codeDrafts[entry.id]
      : entry.code;

    return (
      <article key={entry.id} className="fjc-card fjc-card--program">
        <div className="fjc-card-header">
          <div className="fjc-card-actions">
            <div className="fjc-card-actions-group">
              <button className="fjc-icon" onClick={() => moveEntryWithinSection(entry.id, -1)} aria-label="Monter dans la section">↑</button>
              <button className="fjc-icon" onClick={() => moveEntryWithinSection(entry.id, 1)} aria-label="Descendre dans la section">↓</button>
              <button className="fjc-icon" onClick={() => moveEntryToAdjacentSection(entry.id, -1)} aria-label="Section précédente">←</button>
              <button className="fjc-icon" onClick={() => moveEntryToAdjacentSection(entry.id, 1)} aria-label="Section suivante">→</button>
            </div>
            <div className="fjc-card-actions-end">
              <button
                className="fjc-icon"
                onClick={() => duplicateEntry(entry)}
                aria-label="Dupliquer"
              >
                ⧉
              </button>
              <button
                className="fjc-icon fjc-icon--danger fjc-card-actions-delete"
                onClick={() => removeEntry(entry)}
                aria-label="Supprimer"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <label className="fjc-label">
          Code (Salesforce)
          <input
            className="fjc-input"
            type="text"
            value={codeValue}
            onChange={(event) => updateCodeDraft(entry.id, event.target.value)}
            onBlur={(event) => commitCodeDraft(entry, event.target.value)}
            placeholder="DROIT / disabled1"
          />
        </label>

        <label className="fjc-label">
          Intitulé sur le formulaire
          <input
            type='text'
            className="fjc-textarea"
            rows={2}
            value={linesToText(entry.labels, { preserveHoles: false })}
            onChange={(event) =>
              updateEntry(entry.id, { labels: normalizeLinesEditable(event.target.value, { preserveHoles: false }) })
            }
            placeholder="Une ligne par libellé"
          />
        </label>

        {renderLevelSelector(entry)}

        {renderYearSelector(entry)}

        <label className="fjc-label">
          Parcours
          <textarea
            className="fjc-textarea"
            rows={2}
            value={linesToText(entry.tracks, { preserveHoles: false })}
            onChange={(event) =>
              updateEntry(entry.id, { tracks: normalizeLinesEditable(event.target.value, { preserveHoles: false }) })
            }
            placeholder="Une ligne par parcours"
          />
        </label>
      </article>
    );
  }

  function renderSectionCard(entry) {
    return (
      <article key={entry.id} className="fjc-card fjc-card--section">
        <div className="fjc-card-header" />

        <label className="fjc-label">
          Intitulé sur le formulaire
          <input
            type='text'
            className="fjc-textarea"
            rows={2}
            value={linesToText(entry.labels, { preserveHoles: false })}
            onChange={(event) =>
              updateEntry(entry.id, { labels: normalizeLinesEditable(event.target.value, { preserveHoles: false }) })
            }
            placeholder="Une ligne par libellé"
            readOnly
          />
        </label>

        {renderLevelSelector(entry)}

        {renderYearSelector(entry)}
      </article>
    );
  }

  function renderSectionBlock(section, programs) {
    const groups = groupProgramsByCode(programs);
    return (
      <div key={section.id} className="fjc-section-block">
        <button
          className="fjc-button fjc-button--section-add"
          onClick={() => addProgramToSection(section.id)}
        >
          Ajouter formation {getSectionLabel(section)}
        </button>
        {renderSectionCard(section)}
        <div className="fjc-section-groups">
          {groups.map((group, groupIndex) => {
            const groupKey = `${section.id}::${group.key}`;
            const isCollapsed = collapsedGroups[groupKey] !== false;
            const toneClass = groupIndex % 2 === 0 ? 'fjc-group--tone-a' : 'fjc-group--tone-b';
            return (
              <div className={`fjc-group ${toneClass}`} key={groupKey}>
                <div className="fjc-group-header">
                  <div className="fjc-group-meta">
                    <span className="fjc-group-kicker">Code Salesforce</span>
                    <span className="fjc-group-code">{group.label}</span>
                    <span className="fjc-group-count">{formatGroupCount(group.items.length)}</span>
                  </div>
                  <button
                    className="fjc-group-toggle"
                    onClick={() => toggleGroup(groupKey)}
                    aria-label={isCollapsed ? `Afficher le groupe ${group.label}` : `Masquer le groupe ${group.label}`}
                  >
                    {isCollapsed ? 'Afficher' : 'Masquer'}
                  </button>
                </div>
                {!isCollapsed && (
                  <div className="fjc-group-items">
                    {group.items.map((item) => renderProgramCard(item))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderColumn(column, columnIndex) {
    const blocks = [];
    let currentSection = null;
    let currentPrograms = [];

    column.forEach((entry) => {
      if (entry.kind === 'section') {
        if (currentSection) {
          blocks.push({ section: currentSection, programs: currentPrograms });
        }
        currentSection = entry;
        currentPrograms = [];
        return;
      }
      currentPrograms.push(entry);
    });

    if (currentSection) {
      blocks.push({ section: currentSection, programs: currentPrograms });
    }

    return (
      <div className="fjc-column" key={column[0]?.id || `column-${columnIndex}`}>
        {blocks.length
          ? blocks.map((block) => renderSectionBlock(block.section, block.programs))
          : column.map((entry) => renderProgramCard(entry))}
      </div>
    );
  }

  return (
    <div className="fjc-admin">
      <header className="fjc-header">
        <div>
          <h1>Administration formulaire Je Candidate</h1>
          <p className="fjc-sub">Gestion des formations.</p>
        </div>
        <div className="fjc-controls">
          <label className="fjc-label">
            Token admin
            <input
              className="fjc-input"
              type="password"
              value={token}
              onChange={(event) => handleTokenChange(event.target.value)}
              placeholder="ADMIN_CATALOG_TOKEN"
            />
          </label>
          <div className="fjc-actions">
            <button className="fjc-button" onClick={loadCatalog}>Recharger</button>
            <button className="fjc-button fjc-button--primary" onClick={saveCatalog} disabled={!dirty}>
              Enregistrer
            </button>
            <button className="fjc-button fjc-button--ghost" onClick={fetchJsPreview}>
              Voir le JS
            </button>
          </div>
          {status.message && (
            <p className={`fjc-status fjc-status--${status.type}`}>
              {status.message}
            </p>
          )}
        </div>
      </header>

      <section className="fjc-panel fjc-panel--compact">
        <div className="fjc-panel-header">
          <div>
            <h2>Référentiel</h2>
            <p>Années disponibles pour les formations.</p>
          </div>
        </div>
        <div className="fjc-referentials">
          <div className="fjc-ref-block">
            <h3>Années disponibles</h3>
            <p className="fjc-ref-hint">Glisser-déposer pour réordonner.</p>
            <div
              className="fjc-chip-list"
              onDragOver={(event) => {
                if (!availableYears.length) return;
                event.preventDefault();
                setDragOverYear(null);
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (draggedYear) {
                  moveAvailableYear(draggedYear, null);
                }
                setDraggedYear(null);
                setDragOverYear(null);
              }}
            >
              {availableYears.length ? (
                availableYears.map((year) => (
                  <span
                    className={`fjc-chip${draggedYear === year ? ' is-dragging' : ''}${dragOverYear === year ? ' is-over' : ''}`}
                    key={year}
                    draggable
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = 'move';
                      event.dataTransfer.setData('text/plain', year);
                      setDraggedYear(year);
                      setDragOverYear(year);
                    }}
                    onDragEnd={() => {
                      setDraggedYear(null);
                      setDragOverYear(null);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      if (dragOverYear !== year) {
                        setDragOverYear(year);
                      }
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      if (draggedYear && draggedYear !== year) {
                        moveAvailableYear(draggedYear, year);
                      }
                      setDraggedYear(null);
                      setDragOverYear(null);
                    }}
                  >
                    {year}
                    <button
                      type="button"
                      className="fjc-chip-remove"
                      onClick={() => removeAvailableYear(year)}
                      aria-label={`Supprimer ${year}`}
                      draggable={false}
                      onDragStart={(event) => event.stopPropagation()}
                    >
                      ×
                    </button>
                  </span>
                ))
              ) : (
                <span className="fjc-empty">Aucune année définie.</span>
              )}
            </div>
            <div className="fjc-inline-form">
              <input
                className="fjc-input"
                type="text"
                value={yearDraft}
                onChange={(event) => setYearDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addAvailableYear(yearDraft);
                  }
                }}
                placeholder="2028-2029"
              />
              <button
                type="button"
                className="fjc-button fjc-button--small"
                onClick={() => addAvailableYear(yearDraft)}
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="fjc-panel">
        <div className="fjc-panel-header">
          <div>
            <h2>Catalogue</h2>
          </div>
        </div>

        {columns.length > MAX_VISIBLE_COLUMNS && (
          <div className="fjc-column-nav">
            <button
              className="fjc-button fjc-button--ghost"
              onClick={() => setColumnOffset((prev) => Math.max(0, prev - 1))}
              disabled={columnOffset === 0}
            >
              ← Colonnes
            </button>
            <p className="fjc-column-count">
              {columnOffset + 1}–{Math.min(columnOffset + MAX_VISIBLE_COLUMNS, columns.length)} / {columns.length}
            </p>
            <button
              className="fjc-button fjc-button--ghost"
              onClick={() =>
                setColumnOffset((prev) => Math.min(columns.length - MAX_VISIBLE_COLUMNS, prev + 1))
              }
              disabled={columnOffset + MAX_VISIBLE_COLUMNS >= columns.length}
            >
              Colonnes →
            </button>
          </div>
        )}

        <div className="fjc-columns">
          {visibleColumns.map((column, columnIndex) => renderColumn(column, columnIndex))}
        </div>
      </section>

      {showPreview && (
        <section className="fjc-preview">
          <div className="fjc-panel-header">
            <div>
              <h2>Sortie JS</h2>
              <p>Format identique au snippet attendu par l’endpoint.</p>
            </div>
            <div className="fjc-actions">
              <button className="fjc-button" onClick={copyPreview}>Copier</button>
              <button className="fjc-button fjc-button--ghost" onClick={() => setShowPreview(false)}>
                Fermer
              </button>
            </div>
          </div>
          <textarea className="fjc-preview-area" value={jsPreview} readOnly rows={14} />
        </section>
      )}
    </div>
  );
}
